import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MoonStar, Send } from "lucide-react";
import { registerQuietHoursHandler } from "@/lib/smsQuietHoursBridge";

// Global, always-mounted prompt for the SMS Quiet Hours gate (9PM-8AM America/Chicago).
// Any outgoing-text-triggering request that hits a 428 from the backend routes
// through here via the axios interceptor in App.js — the user must explicitly
// choose Send Now or Wait Until 8AM before the original request is retried.
export const SmsQuietHoursDialog = () => {
  const [detail, setDetail] = useState(null);
  const resolverRef = useRef(null);

  useEffect(() => {
    registerQuietHoursHandler((detail) => {
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setDetail(detail);
      });
    });
  }, []);

  const choose = (decision) => {
    setDetail(null);
    if (resolverRef.current) {
      resolverRef.current(decision);
      resolverRef.current = null;
    }
  };

  return (
    <Dialog open={!!detail} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        data-testid="sms-quiet-hours-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-indigo-600">
            <MoonStar className="h-5 w-5" />
            <DialogTitle>Quiet Hours Active</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-slate-700 text-base">
            {detail?.message || "This will text during quiet hours."}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Texts are held between 9PM and 8AM (America/Chicago) unless you choose to send now.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            data-testid="sms-quiet-hours-wait-btn"
            onClick={() => choose("queue")}
          >
            <MoonStar className="h-4 w-4 mr-2" />
            Wait Until 8AM
          </Button>
          <Button
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            data-testid="sms-quiet-hours-send-now-btn"
            onClick={() => choose("send_now")}
          >
            <Send className="h-4 w-4 mr-2" />
            Send Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
